import {IDepot, IRegion, ISupplierUserProfile} from 'constants/interfaces';
import {Controller, useForm} from 'react-hook-form';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@material-ui/core';
import React from 'react';
import {GOOGLE_API_KEY} from 'constants/index';
import GooglePlacesLocationSearch from 'components/GooglePlacesLocationSearch/GooglePlacesLocationSearch';
import {formattedDepotAddress} from 'screens/Dashboard/Depots/DepotTableSetup';
import PhoneIcon from '@material-ui/icons/Phone';

export function DepotForm(props: {
  user?: ISupplierUserProfile;
  depot?: IDepot;
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (depot: IDepot) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  smallWidth?: boolean;
  readonly?: boolean;
}) {
  type Submission = IDepot & {
    region: IRegion;
  };

  const defaultRegion: IRegion | undefined = props.depot
    ? {
        address: formattedDepotAddress(props.depot),
        latlong: props.depot?.latitude + ',' + props.depot.longitude,
        province: '',
        country: '',
      }
    : undefined;

  // https://levelup.gitconnected.com/using-react-hook-form-with-material-ui-components-ba42ace9507a
  const {handleSubmit, control} = useForm<Submission>({
    defaultValues: {
      ...props?.depot,
      ...(props.depot && {
        region: defaultRegion,
      }),
    },
  });

  const onSubmit = (data: Submission) => {
    const addressParts = data.region.address
      .split(',')
      .map((line) => line.trim());
    const latitude = data.region.latlong.split(',')[0].substr(0, 10);
    const longitude = data.region.latlong.split(',')[1].substr(0, 10);

    const depot: IDepot = {
      // @ts-ignore
      nulandisId: 0,
      // @ts-ignore
      code: data.name.substr(0, 5),
      brandId: props.user?.brand?.id,
      ...data,
      // @ts-ignore
      region: undefined,
      address1: addressParts[0],
      address2: addressParts[1],
      address3: addressParts[2] || '',
      address4: addressParts[3] || '',
      address5: addressParts[4] || '',
      latitude,
      longitude,
    };
    props.onSubmit(depot);
  };

  return (
    <div className="generic-form-container">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            <Grid item xs={12} style={{marginTop: '-0.5rem'}}>
              <Controller
                name="isActive"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={value}
                        onChange={(event) => onChange(event.target.checked)}
                        disabled={props.readonly || props.isLoading}
                      />
                    }
                    label="Active"
                  />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography variant={'subtitle2'} gutterBottom>
                Contact Information
              </Typography>
            </Grid>
            <Grid item sm={12}>
              <Controller
                name="name"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    autoFocus
                    className="input"
                    label="Depot Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  required: 'Depot name required',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="contact"
                control={control}
                defaultValue=""
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Contact Person Full Name"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                  />
                )}
                rules={{
                  required: 'Contact person full name required',
                }}
              />
            </Grid>
            <Grid item xs={props.smallWidth ? 12 : 6}>
              <Controller
                name="phone"
                control={control}
                defaultValue=""
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    label="Contact Person Phone"
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    disabled={props.readonly || props.isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                rules={{
                  required: 'Contact person phone required',
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="form-section">
            <Grid item sm={12}>
              <Typography variant={'subtitle2'}>Address</Typography>
            </Grid>
            <Grid item sm={12}>
              <Controller
                name="region"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <FormControl error={!!error} fullWidth>
                    <GooglePlacesLocationSearch
                      placeholder="Address"
                      queryCountries={['za']}
                      stylesInput={{height: 46, marginLeft: 0}}
                      googleApiKey={GOOGLE_API_KEY}
                      value={value?.address || ''}
                      onSelect={(region: IRegion) =>
                        !props.readonly ? onChange(region) : null
                      }
                      onCleared={() =>
                        !props.readonly ? onChange(undefined) : null
                      }
                    />
                    {error && <FormHelperText>{error.message}</FormHelperText>}
                    {!error && (
                      <FormHelperText>
                        Type to search for an address in South Africa
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
                rules={{
                  required: 'Address required',
                  validate: (value) => {
                    if (value && value.address) return true;
                    return 'Please select an address from the search results';
                  },
                }}
              />
            </Grid>
          </Grid>
          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className={'secondary-btn'}
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className={'primary-btn'}
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading}
                >
                  {props.isLoading ? (
                    <CircularProgress
                      color={'primary'}
                      size={24}
                      thickness={6}
                    />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
